import React from 'react';

// Reason code for a referral stopped because consent was not given. Shared by
// the two initiation routes, the outcome copy below, and the server
// (CONSENT_NOT_GIVEN_REASON in rms_forms.py) - they must stay identical.
export const CONSENT_NOT_GIVEN = 'CONSENT_NOT_GIVEN';

export const defaultOutcomeCopy = {
  eligible: {
    title: "You've initiated your first referral!",
    body: "Thanks for submitting the referral details. We'll send an invitation email to complete account setup and continue the process.",
    ctaLabel: 'Return to start',
  },
  ineligible: {
    title: "We're not able to accept this referral.",
    body: 'Based on the postcode provided, this referral is currently out of our service area. If you believe this is a mistake, please contact us at support@thinkdivergent.co.uk.',
    reasonMessages: {
      OUT_OF_AREA: 'The postcode appears to be out of our service area.',
      INVALID_POSTCODE: 'The postcode provided appears invalid.',
      RULE_NOT_FOUND: 'Service rules were unavailable. Please try again later or contact support.',
    },
    // Some stops are not about eligibility at all, and the default body talks
    // about postcodes. A reason code listed here replaces the title and body
    // outright, so the person is told what actually happened.
    //
    // COPY GATE: this is service-user facing and has not been through Jasmine's
    // comprehension review.
    reasonOverrides: {
      [CONSENT_NOT_GIVEN]: {
        title: 'Consent is needed before a referral can be made',
        body: "A referral can only go ahead once the child or young person's parent or carer has agreed to it. Where a young person is able to decide for themselves, we need their agreement instead.\n\nPlease talk it through with them, then come back and start again. Nothing about the child has been saved.",
        ctaLabel: 'Return to start',
      },
    },
    ctaLabel: 'Return to start',
  },
};

export function resolveOutcomeCopy(orgId) {
  // Placeholder for per-org overrides in future.
  return defaultOutcomeCopy;
}

/**
 * resolveOutcomeSection
 * ---------------------
 * The title / body / CTA to render for one outcome. A reason code with its own
 * override wins over the generic ineligible copy, because a referral stopped
 * for a reason that is not the postcode must not be told it was the postcode.
 *
 * @param {object} copy - resolved org copy
 * @param {boolean} isEligible
 * @param {string} reason - reason code from the server, may be empty
 */
export function resolveOutcomeSection(copy, isEligible, reason) {
  if (isEligible) return copy.eligible;
  const override = reason ? copy.ineligible?.reasonOverrides?.[reason] : null;
  return override ? { ...copy.ineligible, ...override } : copy.ineligible;
}

/**
 * formatEligibleBody
 * ------------------
 * Builds an organisation-agnostic eligible body element that includes the
 * recipient email address (bolded) when we know it. This keeps templating
 * concerns out of the page component while preserving a sensible default
 * fallback when an email was not captured or is unavailable in localStorage.
 * 
 * @param {string} email - The recipient's email address
 * @returns {string|JSX.Element} Plain string fallback or JSX with bold email
 */
export function formatEligibleBody(email) {
  const safeEmail = (email || '').trim();
  if (!safeEmail) {
    return defaultOutcomeCopy.eligible.body;
  }
  return (
    <>
      You will have received an email from <strong>ThinkDivergent</strong> to <strong>{safeEmail}</strong> with instructions on how to complete your account set up and continue the referral process.
      <br /><br />
      If you can't find the email, check your junk or spam folder.
    </>
  );
}
